// ClothedEye Global Constants
// Official brand colors and size definitions

// Category Colors
export const CATEGORY_COLORS = {
  DINO: '#f38b79',      // Dino Orange
  CRYPTID: '#b1b930',   // Cryptid Green  
  CARTOON: '#e47425'    // Cartoon Orange
};

// Vinyl Colors (for scene builder and vinyl representation)
export const VINYL_COLORS = {
  RED: '#be0226',
  ORANGE: '#f46c22',
  YELLOW: '#f2ca10',
  DOZER_YELLOW: '#f99700',
  GREEN: '#6ca844',
  FOREST_GREEN: '#074c2d',
  ICE_BLUE: '#329fd8',
  SKY_BLUE: '#0476bc',
  BLUE: '#1a3877',
  PURPLE: '#412b73',
  VIOLET: '#612b77',
  PINK: '#ff5584',
  NUT_BROWN: '#b05e24',
  BROWN: '#463120',
  // Missing colors - best guesses
  BLACK: '#1a1a1a',  // Matte Black
  WHITE: '#f5f5f5',  // Matte White
  GRAY: '#808080',
  SILVER_METALLIC: '#c0c0c0',
  GOLD_METALLIC: '#ffd700',
  COPPER_METALLIC: '#b87333',
  GRAY_HOLOGRAPHIC: '#808080'
};

// Size Colors
export const SIZE_COLORS = {
  ATOM: '#330033',      // Dark Purple
  PIXEL: '#663399',     // Purple
  NANO: '#6a5acd',      // Slate Blue
  MICRO: '#daa520',     // Goldenrod
  SMALL: '#20b2aa',     // Light Sea Green
  MEDIUM: '#03a9f4',    // Light Blue
  LARGE: '#4fc3f7',     // Light Blue (lighter)
  JUMBO: '#b3e5fc',     // Light Cyan
  MAX: '#ff4500'        // Orange Red
};

// Size definitions with scales
export const SIZES = [
  { name: 'ATOM', scale: 1/600, color: SIZE_COLORS.ATOM },
  { name: 'PIXEL', scale: 1/300, color: SIZE_COLORS.PIXEL },
  { name: 'NANO', scale: 1/175, color: SIZE_COLORS.NANO },
  { name: 'MICRO', scale: 1/107, color: SIZE_COLORS.MICRO },
  { name: 'SMALL', scale: 1/56, color: SIZE_COLORS.SMALL },
  { name: 'MEDIUM', scale: 1/32, color: SIZE_COLORS.MEDIUM },
  { name: 'LARGE', scale: 1/18, color: SIZE_COLORS.LARGE },
  { name: 'JUMBO', scale: 1/10, color: SIZE_COLORS.JUMBO },
  { name: 'MAX', scale: 1/6, color: SIZE_COLORS.MAX }
];

// Get size color by name
export const getSizeColor = (sizeName) => {
  return SIZE_COLORS[sizeName] || '#666666';
};

// Get category color by name
export const getCategoryColor = (categoryName) => {
  return CATEGORY_COLORS[categoryName] || '#666666';
};

// Get vinyl color by name
export const getVinylColor = (colorName) => {
  return VINYL_COLORS[colorName] || '#666666';
};

/**
 * Get the largest available size from a list of size names
 * Uses the SIZES array order to determine which is largest
 * @param {Array<string>} availableSizes - Array of size names
 * @returns {string|null} - The largest size name, or null if none found
 */
export const getLargestAvailableSize = (availableSizes) => {
  if (!availableSizes || availableSizes.length === 0) return null;

  // Create a map of size names to their index in SIZES array (larger
  // index = larger size)
  const sizeOrder = {};
  SIZES.forEach((size, index) => {
    sizeOrder[size.name.toUpperCase()] = index;
  });

  // Find the size with the largest index
  let largestSize = null;
  let largestIndex = -1;

  availableSizes.forEach(sizeName => {
    const normalizedName = sizeName.toUpperCase().split(/[\s($]/)[0];
    const index = sizeOrder[normalizedName];

    if (index !== undefined && index > largestIndex) {
      largestIndex = index;
      largestSize = sizeName;
    }
  });

  return largestSize;
};

export const getSmallestAvailableSize = (availableSizes) => {
  if (!availableSizes || availableSizes.length === 0) return null;

  // Create a map of size names to their index in SIZES array (smaller
  // index = smaller size)
  const sizeOrder = {};
  SIZES.forEach((size, index) => {
    sizeOrder[size.name.toUpperCase()] = index;
  });

  // Find the size with the smallest index
  let smallestSize = null;
  let smallestIndex = Infinity;

  availableSizes.forEach(sizeName => {
    const normalizedName = sizeName.toUpperCase().split(/[\s($]/)[0];
    const index = sizeOrder[normalizedName];

    if (index !== undefined && index < smallestIndex) {
      smallestIndex = index;
      smallestSize = sizeName;
    }
  });

  return smallestSize;
};

// Real Etsy API Constants
export const ETsy_CONSTANTS = {
  SHOP_ID: '6798191', // Real ClothedEye shop ID
  API_KEY: 'ef8nyg615x219dlpre1hn6i1',
  SHARED_SECRET: 'bg67dx015m',
  APP_NAME: 'Clothed Eye Scene Generator'
};

// Real listing IDs (replace with actual listing IDs from your shop)
export const REAL_LISTING_IDS = {
  T_REX: '846621034', // T-Rex listing ID
  // Add more real listing IDs as needed
};

// Real property IDs (these need to be discovered dynamically)
export const PROPERTY_IDS = {
  // These will be discovered via API calls
  COLOR: null, // Will be discovered
  SIZE: null,  // Will be discovered
  STYLE: null  // Will be discovered
};

export default {
  CATEGORY_COLORS,
  VINYL_COLORS,
  SIZE_COLORS,
  SIZES,
  getSizeColor,
  getCategoryColor,
  getVinylColor,
  getLargestAvailableSize,
  getSmallestAvailableSize
}; 